import axios from "axios";
import API_BASE_URL from "../../../config/apiConfig";

const API_URL = `${API_BASE_URL}/api/pcp`;

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

/**
 * Maneja la respuesta de operaciones de escritura.
 * Si result === 0, retorna el objeto completo.
 * Si result > 0 o result === -1, lanza un error con el mensaje del backend.
 */
const handleWriteResponse = (response) => {
  const data = response.data;
  if (data.result === 0) {
    return data;
  }
  const error = new Error(data.message || "Error inesperado del servidor");
  error.result = data.result;
  throw error;
};

// ─────────────────────────────────────────────────────────────────────────────
// WRITE OPERATIONS (POST / DELETE)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Asigna un paciente a un PCP en una location específica.
 *
 * @param {Object} params
 * @param {number} params.cipId - ID del paciente (CIP)
 * @param {number} params.employeePcpId - ID del empleado PCP al que se asigna
 * @param {number} params.locationId - ID de la location donde se asigna
 * @returns {Promise<Object>} Resultado de la operación
 *
 * Ejemplo de llamada:
 * POST /api/pcp/assignments
 * Body:
 * {
 *   "cipId": 45012,
 *   "employeePcpId": 301,
 *   "locationId": 5
 * }
 *
 * Respuesta exitosa (200):
 * {
 *   "result": 0,
 *   "message": "Patient successfully assigned to PCP"
 * }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Patient not found" }
 * { "result": 2, "message": "PCP not found" }
 * { "result": 3, "message": "Location not found" }
 * { "result": 4, "message": "Patient already assigned to this PCP" }
 * { "result": 5, "message": "PCP has reached maximum patient capacity" }
 * { "result": 6, "message": "Invalid cipId" }
 * { "result": 7, "message": "Invalid employeePcpId" }
 * { "result": 8, "message": "Invalid locationId" }
 * { "result": 9, "message": "Patient is inactive" }
 * { "result": 10, "message": "PCP is inactive at this location" }
 * { "result": -1, "message": "Unexpected error" }
 */
export const assignPatient = async ({ cipId, employeePcpId, locationId }) => {
  const response = await axios.post(
    `${API_URL}/assignments`,
    { cipId, employeePcpId, locationId },
    authHeaders()
  );
  return handleWriteResponse(response);
};

/**
 * Desasigna un paciente de su PCP actual, dejándolo sin asignación.
 *
 * @param {number} cipId - ID del paciente (CIP) a desasignar
 * @returns {Promise<Object>} Resultado de la operación
 *
 * Ejemplo de llamada:
 * DELETE /api/pcp/assignments/45012
 *
 * Respuesta exitosa (200):
 * {
 *   "result": 0,
 *   "message": "Patient successfully unassigned"
 * }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Patient not found" }
 * { "result": 2, "message": "Patient is not currently assigned" }
 * { "result": -1, "message": "Unexpected error" }
 */
export const unassignPatient = async (cipId) => {
  const response = await axios.delete(
    `${API_URL}/assignments/${cipId}`,
    authHeaders()
  );
  return handleWriteResponse(response);
};

/**
 * Transfiere todos los pacientes de un PCP/location a otro PCP/location.
 * Útil cuando un PCP se va o se redistribuyen cargas de trabajo.
 *
 * @param {Object} params
 * @param {number} params.fromEmployeePcpId - ID del PCP origen
 * @param {number} params.fromLocationId - ID de la location origen
 * @param {number} params.toEmployeePcpId - ID del PCP destino
 * @param {number} params.toLocationId - ID de la location destino
 * @returns {Promise<Object>} Resultado con cantidad de pacientes transferidos
 *
 * Ejemplo de llamada:
 * POST /api/pcp/assignments/transfer
 * Body:
 * {
 *   "fromEmployeePcpId": 301,
 *   "fromLocationId": 5,
 *   "toEmployeePcpId": 405,
 *   "toLocationId": 5
 * }
 *
 * Respuesta exitosa (200):
 * {
 *   "result": 0,
 *   "message": "Successfully reassigned 127 patients from Dr. Smith to Dr. Johnson"
 * }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Source PCP not found" }
 * { "result": 2, "message": "Destination PCP not found" }
 * { "result": 3, "message": "Source location not found" }
 * { "result": 4, "message": "Destination location not found" }
 * { "result": 5, "message": "No patients to transfer" }
 * { "result": 6, "message": "Destination PCP cannot accommodate all patients" }
 * { "result": -1, "message": "Unexpected error" }
 */
export const transferPatients = async ({
  fromEmployeePcpId,
  fromLocationId,
  toEmployeePcpId,
  toLocationId,
}) => {
  const response = await axios.post(
    `${API_URL}/assignments/transfer`,
    { fromEmployeePcpId, fromLocationId, toEmployeePcpId, toLocationId },
    authHeaders()
  );
  return handleWriteResponse(response);
};

/**
 * Desasigna todos los pacientes de una location específica.
 * Operación masiva para cuando se cierra o reorganiza una location.
 *
 * @param {number} locationId - ID de la location cuyos pacientes se desasignarán
 * @returns {Promise<Object>} Resultado con cantidad de pacientes desasignados
 *
 * Ejemplo de llamada:
 * DELETE /api/pcp/assignments/location/5
 *
 * Respuesta exitosa (200):
 * {
 *   "result": 0,
 *   "message": "Successfully unassigned 342 patients from location Fort Myers"
 * }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Location not found" }
 * { "result": 2, "message": "No assigned patients in this location" }
 * { "result": -1, "message": "Unexpected error" }
 */
export const unassignLocation = async (locationId) => {
  const response = await axios.delete(
    `${API_URL}/assignments/location/${locationId}`,
    authHeaders()
  );
  return handleWriteResponse(response);
};

/**
 * Ejecuta el algoritmo de auto-asignación de pacientes sin PCP.
 * Distribuye pacientes orphan según reglas de negocio (capacidad, riesgo, etc.).
 * No requiere body; el backend ejecuta la lógica completa.
 *
 * @returns {Promise<Object>} Resultado con resumen de asignaciones realizadas
 *
 * Ejemplo de llamada:
 * POST /api/pcp/assignments/auto
 * Body: (ninguno)
 *
 * Respuesta exitosa (200):
 * {
 *   "result": 0,
 *   "message": "ORPHANS: 3 | W1: 15 | W2: 42 | W3: 88 | TOTAL: 148"
 * }
 *
 * Posibles errores (400):
 * { "result": 1, "message": "No unassigned patients found" }
 * { "result": 2, "message": "No available PCPs with capacity" }
 * { "result": -1, "message": "Unexpected error" }
 */
export const autoAssign = async () => {
  const response = await axios.post(
    `${API_URL}/assignments/auto`,
    null,
    authHeaders()
  );
  return handleWriteResponse(response);
};

// ─────────────────────────────────────────────────────────────────────────────
// READ OPERATIONS (GET)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Obtiene la lista de PCPs disponibles en una location, con su carga actual
 * y métricas de panel.
 *
 * @param {number} locationId - ID de la location
 * @returns {Promise<Array>} Lista de PCPs con métricas
 *
 * Ejemplo de llamada:
 * GET /api/pcp/pcps/5
 *
 * Respuesta exitosa (200):
 * [
 *   {
 *     "employeePcpId": 301,
 *     "pcpName": "Dr. Maria Garcia",
 *     "title": "MD",
 *     "locationId": 5,
 *     "centerName": "Fort Myers",
 *     "maxPatient": 250,
 *     "currentLoad": 198,
 *     "available": 52,
 *     "highRisk": 34,
 *     "highCost": 12,
 *     "medicare": 145,
 *     "critical": 5,
 *     "ageMin": 22,
 *     "ageMax": 89
 *   }
 * ]
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Location not found" }
 * { "result": -1, "message": "Unexpected error" }
 */
export const getPcpsByLocation = async (locationId) => {
  const response = await axios.get(
    `${API_URL}/pcps/${locationId}`,
    authHeaders()
  );
  return response.data;
};

/**
 * Obtiene la lista de pacientes sin PCP asignado en una location.
 *
 * @param {number} locationId - ID de la location
 * @returns {Promise<Array>} Lista de pacientes no asignados
 *
 * Ejemplo de llamada:
 * GET /api/pcp/unassigned/5
 *
 * Respuesta exitosa (200):
 * [
 *   {
 *     "cipId": 45012,
 *     "memberFullName": "John Smith",
 *     "age": 67,
 *     "gender": "M",
 *     "locationId": 5,
 *     "centerName": "Fort Myers"
 *   }
 * ]
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Location not found" }
 * { "result": -1, "message": "Unexpected error" }
 */
export const getUnassignedPatients = async (locationId) => {
  const response = await axios.get(
    `${API_URL}/unassigned/${locationId}`,
    authHeaders()
  );
  return response.data;
};

/**
 * Obtiene los pacientes asignados a un PCP en una location específica.
 *
 * @param {number} pcpId - ID del empleado PCP
 * @param {number} locationId - ID de la location
 * @returns {Promise<Array>} Lista de pacientes asignados con detalles
 *
 * Ejemplo de llamada:
 * GET /api/pcp/assignments/301/5
 *
 * Respuesta exitosa (200):
 * [
 *   {
 *     "assignmentId": 10234,
 *     "cipId": 45012,
 *     "memberFullName": "John Smith",
 *     "age": 67,
 *     "gender": "M",
 *     "assignmentSource": "manual",
 *     "crossLocation": false,
 *     "assignedDate": "2025-11-15T10:30:00Z"
 *   }
 * ]
 *
 * Posibles errores (400):
 * { "result": 1, "message": "PCP not found" }
 * { "result": 2, "message": "Location not found" }
 * { "result": -1, "message": "Unexpected error" }
 */
export const getAssignedPatients = async (pcpId, locationId) => {
  const response = await axios.get(
    `${API_URL}/assignments/${pcpId}/${locationId}`,
    authHeaders()
  );
  return response.data;
};

/**
 * Obtiene el historial de asignaciones de un paciente.
 * Muestra todos los cambios de PCP que ha tenido el paciente.
 *
 * @param {number} cipId - ID del paciente (CIP)
 * @returns {Promise<Array>} Historial de asignaciones del paciente
 *
 * Ejemplo de llamada:
 * GET /api/pcp/history/45012
 *
 * Respuesta exitosa (200):
 * [
 *   {
 *     "historyId": 5001,
 *     "employeePcpId": 301,
 *     "pcpName": "Dr. Maria Garcia",
 *     "locationId": 5,
 *     "centerName": "Fort Myers",
 *     "assignmentSource": "manual",
 *     "changeReason": "PCP transfer",
 *     "crossLocation": false,
 *     "assignedFrom": "2025-06-01T00:00:00Z",
 *     "assignedTo": "2025-11-15T10:30:00Z",
 *     "employeeCreate": 102,
 *     "actorName": "Admin User",
 *     "createDate": "2025-11-15T10:30:00Z"
 *   }
 * ]
 *
 * Posibles errores (400):
 * { "result": 1, "message": "Patient not found" }
 * { "result": -1, "message": "Unexpected error" }
 */
export const getPatientHistory = async (cipId) => {
  const response = await axios.get(
    `${API_URL}/history/${cipId}`,
    authHeaders()
  );
  return response.data;
};

/**
 * Obtiene el resumen de asignaciones por location.
 * Muestra totales de pacientes asignados, cross-location y sin asignar.
 *
 * @returns {Promise<Array>} Resumen por location
 *
 * Ejemplo de llamada:
 * GET /api/pcp/summary
 *
 * Respuesta exitosa (200):
 * [
 *   {
 *     "locationId": 5,
 *     "centerName": "Fort Myers",
 *     "assignedPatients": 1250,
 *     "crossLocationPatients": 45,
 *     "unassignedPatients": 89
 *   }
 * ]
 *
 * Posibles errores (400):
 * { "result": -1, "message": "Unexpected error" }
 */
export const getAssignmentSummary = async () => {
  const response = await axios.get(`${API_URL}/summary`, authHeaders());
  return response.data;
};
